'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';

import NextImage, { ImageProps as NextImageProps } from 'next/image';

import { FALLBACK_IMAGE_URL } from '@constants/constants';

/**
 * In carousel/slider implementations (e.g. Swiper), slides are often kept in the DOM
 * even when they are outside of the viewport. Because of this, native
 * `loading="lazy"` may not behave as expected and images can be fetched earlier
 * than intended.
 *
 * This component enforces true lazy loading by using IntersectionObserver and
 * only assigning the real `src` when the image is about to enter the viewport.
 */
export interface LazyImageProps extends Omit<NextImageProps, 'src'> {
  src?: NextImageProps['src'];
  fallbackSrc?: string;
  rootMargin?: string;
}

const LazyImage = ({
  src = FALLBACK_IMAGE_URL,
  fallbackSrc = FALLBACK_IMAGE_URL,
  rootMargin = '300px',
  ...rest
}: LazyImageProps) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const resolvedSrc = useMemo(() => src || fallbackSrc, [src, fallbackSrc]);

  const [shouldLoad, setShouldLoad] = useState(false);
  const [imgSrc, setImgSrc] = useState<NextImageProps['src'] | null>(null);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (shouldLoad) return;

    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      setShouldLoad(true);
      return;
    }

    const el = wrapperRef.current;
    if (!el) return;

    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setShouldLoad(true);
          io.disconnect();
        }
      },
      { root: null, rootMargin, threshold: 0.01 }
    );

    io.observe(el);
    return () => io.disconnect();
  }, [rootMargin, shouldLoad]);

  useEffect(() => {
    if (shouldLoad) {
      setImgSrc(resolvedSrc);
    }
  }, [shouldLoad, resolvedSrc]);

  const handleOnError = () => {
    if (imgSrc !== fallbackSrc) {
      setImgSrc(fallbackSrc);
    } else {
      setLoading(false);
    }
  };

  const handleOnLoad = () => {
    setLoading(false);
  };

  return (
    <div
      ref={wrapperRef}
      style={{
        position: 'relative',
        display: 'inline-block',
        width: rest.fill ? '100%' : undefined,
        height: rest.fill ? '100%' : undefined,
      }}
    >
      {loading && (
        <div
          className='image-skeleton'
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background:
              'linear-gradient(90deg, #ececec 25%, #f5f5f5 50%, #ececec 75%)',
            backgroundSize: '200% 100%',
            animation: 'skeleton-loading 1.2s ease-in-out infinite',
            borderRadius: '8px',
            zIndex: 2,
          }}
        />
      )}

      <style>{`
        @keyframes skeleton-loading {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>

      {imgSrc && (
        <NextImage
          {...rest}
          src={imgSrc}
          data-src={src}
          onError={handleOnError}
          onLoad={handleOnLoad}
          loading='eager'
        />
      )}
    </div>
  );
};

export default LazyImage;
